import React, { useCallback } from 'react';
import filter from 'lodash/filter';
import get from 'lodash/get';
import some from 'lodash/some';
import styled from 'styled-components';

import ExternalLink from 'src/presentation/components/pages/shared/ExternalLink';
import RouterLinkButton from 'src/presentation/components/RouterLinkButton';
import TablePage, { TablePageRender } from 'src/presentation/components/pages/shared/TablePage';
import TeamEditor from './TeamEditor';
import teamsResource from 'src/actions/teams/teamsResource';
import useTeams from 'src/presentation/components/pages/people/teams/hooks/useTeams';
import { Delete } from 'src/presentation/components/pages/shared/rowActions/Delete';
import { Edit } from 'src/presentation/components/pages/shared/rowActions/Edit';

const filterFunc = (items, searchString) => {
  const searchPaths = ['name'];
  if (searchString) {
    const search = searchString.toLowerCase().trim();
    return filter(items, item =>
      some(searchPaths, columnKey => get(item, columnKey, '').toLowerCase().includes(search))
    );
  }
  return items;
};

const renderRowActions = ({ item }) => (
  <RowActions>
    <Edit id={item.id} />
    <Delete
      confirmationText="Delete team"
      deleteAction={teamsResource.effects.deleteTeam}
      description={
        <>
          Are you sure you want to delete <strong>{item.name}</strong>? This cannot be undone.
        </>
      }
      id={item.id}
      submitText="Delete team"
    />
  </RowActions>
);

const columnConfig = [
  {
    attr: 'name',
    label: 'Name',
    sorting: true,
    customColumnStyle: () => ({ width: '250px' }),
  },
  {
    attr: 'memberCount',
    label: 'Members',
    sorting: ({ memberCount }) => memberCount,
    defaultValue: 0,
    customStyle: () => ({ textAlign: 'right' }),
    customColumnStyle: () => ({ width: '100px', textAlign: 'right' }),
  },
  {
    attr: 'description',
    label: 'Purpose',
    sorting: true,
    customColumnStyle: () => ({ width: '300px' }),
  },
];

const sortingConfig = { attr: 'name' };

const Teams: TablePageRender = ({ root }) => {
  const { isLoading, teamsList, resetErrors, errors } = useTeams();
  const dataAidPrefix = 'admin-teams-page';

  const createTeamAction = useCallback(
    ({ dataAid }) => (
      <RouterLinkButton data-aid={`${dataAid}-createButton`} to={`${root}/new`}>
        Create team
      </RouterLinkButton>
    ),
    [root]
  );

  const description = (
    <div>
      Organize people who collaborate with each other into teams and track group performance via Liveboards and
      Reporting. Learn how to{' '}
      <ExternalLink label="create and manage teams" to="https://help.gladly.com/docs/create-and-manage-teams" />
    </div>
  );

  return (
    <TablePage
      actions={createTeamAction({ dataAid: 'team' })}
      columnConfig={columnConfig}
      data-aid={dataAidPrefix}
      description={description}
      errors={errors}
      filterFunc={filterFunc}
      editor={TeamEditor}
      isLoading={isLoading}
      items={teamsList}
      resetErrors={resetErrors}
      renderRowActions={renderRowActions}
      initialSorting={sortingConfig}
      name="Teams"
      root={root}
    />
  );
};

const RowActions = styled.div`
  display: flex;
`;

export default Teams;
